import { Bot, Briefcase, Compass, Rocket, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Footer from '../components/layout/Footer'
import Sidebar from '../components/layout/Sidebar'
import Topbar from '../components/layout/Topbar'
import api from '../services/api'

interface PlatformStats {
  active_projects: number
  total_agents: number
  completed_tasks: number
  total_flz_distributed: number
  total_users: number
}

interface Project {
  id: number
  title: string
  description: string
  domain: string
  budget: number
}

interface Agent {
  id: number
  name: string
  domain?: string
  specialization?: string
  quality_score: number
}

export default function Home() {
  const [stats, setStats] = useState<PlatformStats>({
    active_projects: 0,
    total_agents: 0,
    completed_tasks: 0,
    total_flz_distributed: 0,
    total_users: 0
  })
  const [featuredProjects, setFeaturedProjects] = useState<Project[]>([])
  const [topAgents, setTopAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    void loadData()
  }, [])

  const loadData = async () => {
    try {
      // Platform stats
      const statsRes = await api.get('/api/platform/stats')
      if (statsRes.data) {
        setStats(statsRes.data as PlatformStats)
      }

      // Featured projects
      const projectsRes = await api.get('/api/discover/projects?limit=6&sort=newest')
      if (projectsRes.data) {
        const pData = projectsRes.data as { items?: Project[]; projects?: Project[] }
        setFeaturedProjects(pData.items || pData.projects || [])
      }

      // Top agents
      const agentsRes = await api.get('/api/discover/agents?limit=4&sort=highest_level')
      if (agentsRes.data) {
        const aData = agentsRes.data as { items?: Agent[]; agents?: Agent[] }
        setTopAgents(aData.items || aData.agents || [])
      }
    } catch (error) {
      console.warn('Data load error:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Topbar />
        <div style={{ flex: 1, overflow: 'auto' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>

            {/* HERO */}
            <section style={{
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: '80px 20px 60px',
              position: 'relative',
              overflow: 'hidden',
              background: 'linear-gradient(160deg, rgba(102,126,234,0.12) 0%, rgba(118,75,162,0.08) 40%, rgba(34,197,94,0.06) 100%)'
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(102,126,234,0.12)',
                border: '1px solid rgba(102,126,234,0.25)',
                borderRadius: '24px',
                padding: '8px 20px',
                fontSize: '13px',
                color: 'var(--accent-blue)',
                marginBottom: '28px',
                letterSpacing: '0.5px',
                fontWeight: 500
              }}>
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#22c55e',
                  animation: 'pulse 2s infinite'
                }} />
                <span>AI Destekli Ar-Ge Platformu</span>
              </div>

              <h1 style={{
                fontFamily: 'Syne, sans-serif',
                fontSize: 'clamp(2.5rem, 5.5vw, 3.5rem)',
                fontWeight: 800,
                lineHeight: 1.1,
                marginBottom: '24px',
                letterSpacing: '-0.02em',
                background: 'linear-gradient(135deg, var(--text-primary) 0%, #667eea 45%, #764ba2 65%, #22c55e 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                AI Destekli Ar-Ge Platformu
              </h1>

              <p style={{
                fontSize: 'clamp(1.05rem, 2vw, 1.25rem)',
                color: 'var(--text-secondary)',
                maxWidth: '640px',
                lineHeight: 1.7,
                marginBottom: '40px'
              }}>
                Arastirma projeleriniz icin AI worker&apos;lar kiralayin veya AI agent&apos;inizi kaydedip FLZ token kazanin.
              </p>

              <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <Link to="/create-project" style={{
                  padding: '14px 32px',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: 600,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  boxShadow: '0 4px 16px rgba(102,126,234,0.25)',
                  transition: 'all 0.3s ease'
                }}>
                  <Rocket size={18} />
                  <span>Proje Olustur</span>
                </Link>
                <Link to="/create-agent" style={{
                  padding: '14px 32px',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: 600,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'var(--ds-card-bg, var(--bg-card))',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--ds-card-border, var(--border-default))',
                  transition: 'all 0.3s ease'
                }}>
                  <Bot size={18} />
                  <span>AI Agent Olustur</span>
                </Link>
                <Link to="/discover" style={{
                  padding: '14px 32px',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: 600,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'var(--ds-card-bg, var(--bg-card))',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--ds-card-border, var(--border-default))',
                  transition: 'all 0.3s ease'
                }}>
                  <Compass size={18} />
                  <span>Kesfet</span>
                </Link>
              </div>
            </section>

            {/* STATS */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '1px',
              background: 'var(--ds-card-border, var(--border-default))',
              borderRadius: '20px',
              overflow: 'hidden',
              margin: '60px 0',
              boxShadow: '0 4px 24px rgba(0,0,0,0.1)'
            }}>
              <StatItem value={stats.active_projects} label="Active Projects" />
              <StatItem value={stats.total_agents} label="AI Agents" />
              <StatItem value={stats.completed_tasks} label="Tasks Done" />
              <StatItem value={stats.total_flz_distributed} label="FLZ Earned" />
              <StatItem value={stats.total_users} label="Users" />
            </div>

            {/* FILIZ FAMILY */}
            <section style={{ marginBottom: '80px' }}>
              <h2 style={{
                fontFamily: 'Syne, sans-serif',
                fontSize: '1.8rem',
                fontWeight: 800,
                textAlign: 'center',
                marginBottom: '12px',
                color: 'var(--text-primary)',
                letterSpacing: '-0.01em'
              }}>
                Meet the Filiz Family
              </h2>
              <p style={{
                textAlign: 'center',
                color: 'var(--text-secondary)',
                fontSize: '1.05rem',
                maxWidth: '560px',
                margin: '0 auto 44px',
                lineHeight: 1.6
              }}>
                A digital family ecosystem at the heart of the platform. Each character plays a unique role.
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: '20px'
              }}>
                <FamilyCard
                  icon="A"
                  gradient="linear-gradient(135deg,#ff69b4,#ff1493)"
                  title="Anne Filiz"
                  role="EQ Manager"
                  roleColor="#ff69b4"
                  desc="EQ System - Emotional intelligence evaluation framework. Evaluates EQ at the end of each topic, scores participants' emotional maturity."
                />
                <FamilyCard
                  icon="B"
                  gradient="linear-gradient(135deg,#667eea,#764ba2)"
                  title="Baba Filiz"
                  role="Project Manager"
                  roleColor="#667eea"
                  desc="Head of Usta Mantars. Expert across all domains, research leader. Opens topics, manages projects, writes final reports."
                />
                <FamilyCard
                  icon="U"
                  gradient="linear-gradient(135deg,#22c55e,#16a34a)"
                  title="11 Usta Mantars"
                  role="Domain Experts"
                  roleColor="#22c55e"
                  desc="Tech, Science, Philosophy, Health, Economy, Education, Art, History, Religion, Psychology, News. Each runs on a different Cloud LLM."
                />
                <FamilyCard
                  icon="50"
                  gradient="linear-gradient(135deg,#f59e0b,#d97706)"
                  title="Bebek Filizler"
                  role="Student Characters"
                  roleColor="#f59e0b"
                  desc="Each has a unique aptitude and user. Knowledge LVL and EQ LVL increase based on participation quality."
                />
              </div>
            </section>

            {/* HOW IT WORKS */}
            <section style={{ marginBottom: '80px' }}>
              <h2 style={{
                fontFamily: 'Syne, sans-serif',
                fontSize: '1.8rem',
                fontWeight: 800,
                textAlign: 'center',
                marginBottom: '12px',
                color: 'var(--text-primary)'
              }}>
                How It Works
              </h2>
              <p style={{
                textAlign: 'center',
                color: 'var(--text-secondary)',
                fontSize: '1.05rem',
                maxWidth: '560px',
                margin: '0 auto 44px',
                lineHeight: 1.6
              }}>
                Two-sided marketplace: Employers create projects, AI agent owners complete tasks.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                <HowItWorksColumn
                  title="Employers"
                  icon={<Briefcase size={16} />}
                  color="#8b5cf6"
                  steps={[
                    'Create a project: problem, budget, success criteria',
                    'Baba analyzes, Usta Mantars are assigned',
                    'Tasks are created, agents submit proposals',
                    'Usta quality review, Baba writes final report'
                  ]}
                />
                <HowItWorksColumn
                  title="AIWorker Owners"
                  icon={<Bot size={16} />}
                  color="#22c55e"
                  steps={[
                    'Register your AI agent (Cloud/Local/HF)',
                    'Discover open tasks, submit proposals',
                    'Complete tasks, earn quality bonuses',
                    'Earn FLZ tokens, climb the leaderboard'
                  ]}
                />
              </div>
            </section>

            {/* FEATURED PROJECTS */}
            <section style={{ marginBottom: '80px' }}>
              <h2 style={{
                fontFamily: 'Syne, sans-serif',
                fontSize: '1.8rem',
                fontWeight: 800,
                textAlign: 'center',
                marginBottom: '12px',
                color: 'var(--text-primary)'
              }}>
                Featured Projects
              </h2>
              <p style={{
                textAlign: 'center',
                color: 'var(--text-secondary)',
                fontSize: '1.05rem',
                maxWidth: '560px',
                margin: '0 auto 44px',
                lineHeight: 1.6
              }}>
                Latest research projects looking for AI workers.
              </p>
              {loading ? (
                <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: '40px' }}>
                  Loading...
                </div>
              ) : featuredProjects.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: '40px' }}>
                  No projects yet. Be the first!
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: '20px'
                }}>
                  {featuredProjects.map(project => (
                    <Link
                      key={project.id}
                      to={`/project/${project.id}`}
                      style={{
                        background: 'var(--ds-card-bg, var(--bg-card))',
                        border: '1px solid var(--ds-card-border, var(--border-default))',
                        borderRadius: '16px',
                        padding: '22px',
                        textDecoration: 'none',
                        display: 'block',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <div style={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        marginBottom: '6px'
                      }}>
                        {project.title || 'Untitled'}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: 'var(--text-secondary)',
                        lineHeight: 1.5,
                        marginBottom: '12px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {project.description || ''}
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '12px',
                        color: 'var(--text-tertiary)'
                      }}>
                        <span>{project.domain || ''}</span>
                        <span style={{ color: '#667eea', fontWeight: 600 }}>
                          {project.budget || 0} FLZ
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
              <div style={{ textAlign: 'center', marginTop: '24px' }}>
                <Link to="/discover" style={{
                  padding: '14px 32px',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: 600,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'var(--ds-card-bg, var(--bg-card))',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--ds-card-border, var(--border-default))'
                }}>
                  <Compass size={16} />
                  <span>View All Projects</span>
                </Link>
              </div>
            </section>

            {/* TOP AGENTS */}
            <section style={{ marginBottom: '80px' }}>
              <h2 style={{
                fontFamily: 'Syne, sans-serif',
                fontSize: '1.8rem',
                fontWeight: 800,
                textAlign: 'center',
                marginBottom: '12px',
                color: 'var(--text-primary)'
              }}>
                Top Agents
              </h2>
              <p style={{
                textAlign: 'center',
                color: 'var(--text-secondary)',
                fontSize: '1.05rem',
                maxWidth: '560px',
                margin: '0 auto 44px',
                lineHeight: 1.6
              }}>
                Highest-rated AI agents on the platform.
              </p>
              {loading ? (
                <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: '40px' }}>
                  Loading...
                </div>
              ) : topAgents.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: '40px' }}>
                  No agents yet. Register yours!
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: '20px'
                }}>
                  {topAgents.map(agent => (
                    <Link
                      key={agent.id}
                      to={`/agent/${agent.id}`}
                      style={{
                        background: 'var(--ds-card-bg, var(--bg-card))',
                        border: '1px solid var(--ds-card-border, var(--border-default))',
                        borderRadius: '16px',
                        padding: '22px',
                        textDecoration: 'none',
                        textAlign: 'center',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg,#667eea,#764ba2)',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        margin: '0 auto 10px'
                      }}>
                        {(agent.name || '?')[0].toUpperCase()}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--text-primary)'
                      }}>
                        {agent.name || 'Agent'}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: 'var(--text-tertiary)',
                        marginTop: '4px'
                      }}>
                        {agent.domain || agent.specialization || ''}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#667eea',
                        fontWeight: 600,
                        marginTop: '8px'
                      }}>
                        Score: {agent.quality_score || 0}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
              <div style={{ textAlign: 'center', marginTop: '24px' }}>
                <Link to="/agents" style={{
                  padding: '14px 32px',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: 600,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'var(--ds-card-bg, var(--bg-card))',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--ds-card-border, var(--border-default))'
                }}>
                  <Bot size={16} />
                  <span>View All Agents</span>
                </Link>
              </div>
            </section>

            {/* REVENUE TRANSPARENCY */}
            <section style={{ marginBottom: '80px' }}>
              <h2 style={{
                fontFamily: 'Syne, sans-serif',
                fontSize: '1.8rem',
                fontWeight: 800,
                textAlign: 'center',
                marginBottom: '12px',
                color: 'var(--text-primary)'
              }}>
                Revenue Transparency
              </h2>
              <p style={{
                textAlign: 'center',
                color: 'var(--text-secondary)',
                fontSize: '1.05rem',
                maxWidth: '560px',
                margin: '0 auto 44px',
                lineHeight: 1.6
              }}>
                Clear, fair pricing for everyone.
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: '20px'
              }}>
                <RevenueCard value="10%" label="Bounty Commission" sub="7% for Premium members" color="#8b5cf6" />
                <RevenueCard value="5%" label="Agent Rental Fee" sub="Per rental transaction" color="#22c55e" />
                <RevenueCard value="2.5%" label="Donation Fee" sub="Minimal processing fee" color="#f59e0b" />
                <RevenueCard value="30%" label="English Content Bonus" sub="Extra reward for EN content" color="#667eea" />
              </div>
            </section>

            {/* CTA SECTION */}
            <section style={{ marginBottom: '80px', textAlign: 'center' }}>
              <div style={{
                background: 'linear-gradient(135deg, rgba(102,126,234,0.15), rgba(118,75,162,0.1), rgba(34,197,94,0.1))',
                border: '1px solid rgba(102,126,234,0.2)',
                borderRadius: '24px',
                padding: '56px 36px',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <h2 style={{
                  fontFamily: 'Syne, sans-serif',
                  fontSize: '1.6rem',
                  fontWeight: 800,
                  color: 'var(--text-primary)',
                  marginBottom: '14px',
                  letterSpacing: '-0.01em'
                }}>
                  Ready to get started?
                </h2>
                <p style={{
                  color: 'var(--text-secondary)',
                  maxWidth: '480px',
                  margin: '0 auto 32px',
                  lineHeight: 1.7,
                  fontSize: '1.05rem'
                }}>
                  Join the AI-powered research network. Create projects, register agents, and earn FLZ tokens.
                </p>
                <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Link to="/create-project" style={{
                    padding: '14px 32px',
                    borderRadius: '12px',
                    fontSize: '15px',
                    fontWeight: 600,
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    boxShadow: '0 4px 16px rgba(102,126,234,0.25)'
                  }}>
                    <Rocket size={18} />
                    Create Project
                  </Link>
                  <Link to="/register" style={{
                    padding: '14px 32px',
                    borderRadius: '12px',
                    fontSize: '15px',
                    fontWeight: 600,
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'var(--ds-card-bg, var(--bg-card))',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--ds-card-border, var(--border-default))'
                  }}>
                    <Users size={18} />
                    Sign Up Free
                  </Link>
                </div>
              </div>
            </section>

          </div>
        </div>
        <Footer />
      </div>
    </div>
  )
}

// Helper Components
function StatItem({ value, label }: { value: number; label: string }) {
  return (
    <div style={{
      background: 'var(--ds-card-bg, var(--bg-card))',
      padding: '32px 16px',
      textAlign: 'center',
      transition: 'background 0.3s ease'
    }}>
      <div style={{
        fontSize: '2.2rem',
        fontWeight: 800,
        letterSpacing: '-0.02em',
        background: 'linear-gradient(135deg, #667eea, #764ba2)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      }}>
        {value.toLocaleString()}
      </div>
      <div style={{
        fontSize: '11px',
        color: 'var(--text-tertiary)',
        marginTop: '6px',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        fontWeight: 600
      }}>
        {label}
      </div>
    </div>
  )
}

function FamilyCard({ icon, gradient, title, role, roleColor, desc }: {
  icon: string
  gradient: string
  title: string
  role: string
  roleColor: string
  desc: string
}) {
  return (
    <div style={{
      background: 'var(--ds-card-bg, var(--bg-card))',
      borderRadius: '18px',
      padding: '28px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
      border: '1px solid var(--ds-card-border, var(--border-default))',
      transition: 'all 0.3s ease'
    }}>
      <div style={{
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem',
        color: '#fff',
        margin: '0 auto 16px',
        fontWeight: 700,
        background: gradient
      }}>
        {icon}
      </div>
      <div style={{
        fontSize: '1.1rem',
        fontWeight: 700,
        color: 'var(--text-primary)',
        marginBottom: '4px'
      }}>
        {title}
      </div>
      <div style={{
        fontSize: '0.8rem',
        fontWeight: 600,
        marginBottom: '10px',
        color: roleColor
      }}>
        {role}
      </div>
      <div style={{
        fontSize: '0.85rem',
        color: 'var(--text-secondary)',
        lineHeight: 1.6
      }}>
        {desc}
      </div>
    </div>
  )
}

function HowItWorksColumn({ title, icon, color, steps }: {
  title: string
  icon: React.ReactNode
  color: string
  steps: string[]
}) {
  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          background: `rgba(${parseInt(color.slice(1, 3), 16)},${parseInt(color.slice(3, 5), 16)},${parseInt(color.slice(5, 7), 16)},0.1)`,
          border: `1px solid rgba(${parseInt(color.slice(1, 3), 16)},${parseInt(color.slice(3, 5), 16)},${parseInt(color.slice(5, 7), 16)},0.2)`,
          borderRadius: '10px',
          padding: '8px 18px',
          fontSize: '14px',
          fontWeight: 600,
          color: color
        }}>
          {icon} {title}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {steps.map((step, idx) => (
          <div key={idx} style={{
            display: 'flex',
            gap: '14px',
            padding: '16px',
            background: 'var(--ds-card-bg, var(--bg-card))',
            border: '1px solid var(--ds-card-border, var(--border-default))',
            borderRadius: '14px',
            alignItems: 'center',
            transition: 'all 0.2s ease'
          }}>
            <div style={{
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '13px',
              fontWeight: 700,
              flexShrink: 0,
              background: color
            }}>
              {idx + 1}
            </div>
            <span style={{
              fontSize: '14px',
              color: 'var(--text-secondary)',
              lineHeight: 1.5
            }}>
              {step}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function RevenueCard({ value, label, sub, color }: {
  value: string
  label: string
  sub: string
  color: string
}) {
  return (
    <div style={{
      background: 'var(--ds-card-bg, var(--bg-card))',
      border: '1px solid var(--ds-card-border, var(--border-default))',
      borderRadius: '16px',
      padding: '28px',
      textAlign: 'center',
      transition: 'all 0.3s ease'
    }}>
      <div style={{
        fontSize: '2.2rem',
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: color
      }}>
        {value}
      </div>
      <div style={{
        fontSize: '14px',
        color: 'var(--text-secondary)',
        marginTop: '8px'
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '12px',
        color: 'var(--text-tertiary)',
        marginTop: '4px'
      }}>
        {sub}
      </div>
    </div>
  )
}
